import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlagView } from '@/features/flag/components/FlagView';
import type { FlagContextValues } from '@/features/flag/providers/useFlag';
import { FlagContext } from '@/features/flag/providers/useFlag';
import type { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import { useBottomModal } from '@/features/popup/provider/useBottomModal';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

export const FlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation(LocalizationNameSpace.FLAGS);
  const { showToast } = usePopup();
  const { clearAllBottomModals, showBottomModal } = useBottomModal();

  const flag = useCallback(
    (entityType: FlagEntityTypeEnum, id: string) =>
      showBottomModal({
        title: t('title'),
        expandWithKeyboard: true,
        children: (
          <FlagView
            entityType={entityType}
            id={id}
            onSend={() => {
              clearAllBottomModals();
              showToast({ message: t('toast') });
            }}
          />
        )
      }),
    [clearAllBottomModals, showBottomModal, showToast, t]
  );

  const contextValues: FlagContextValues = useMemo(() => ({ flag }), [flag]);

  return (
    <FlagContext.Provider value={contextValues}>
      {children}
    </FlagContext.Provider>
  );
};
